import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { toast, Toaster } from "sonner";
import {
  Eye, Wind, Coffee, Trophy, Loader2, Sparkles, Check, ArrowRight, ArrowLeft,
  Wine, Lightbulb, X, RotateCcw, Beer, GlassWater, BookmarkPlus, Map,
} from "lucide-react";

const KIND_ICONS = { wine: Wine, beer: Beer, spirit: GlassWater };
const KIND_TINT = { wine: "#7C1F3D", beer: "#B45309", spirit: "#1F3A5F" };
const STEP_ICONS = { 1: Eye, 2: Wind, 3: Coffee };

export default function Deduce() {
  const { user } = useAuth();
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [kind, setKind] = useState(null);
  const [schema, setSchema] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({});
  const [narrowing, setNarrowing] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    // Freemium: no login required.
  }, [user, navigate]);

  const stepMeta = useMemo(() => {
    const k = kind || "wine";
    return {
      1: { icon: STEP_ICONS[1], label: t("deduce.step1.wine"), subtitle: t(`deduce.step1.${k}Sub`) },
      2: { icon: STEP_ICONS[2], label: t("deduce.step2"),       subtitle: t(`deduce.step2.${k}Sub`) },
      3: { icon: STEP_ICONS[3], label: t("deduce.step3"),       subtitle: t(`deduce.step3.${k}Sub`) },
    };
  }, [kind, t]);

  const start = useCallback(async (selectedKind) => {
    setLoading(true);
    try {
      const sch = await api.get(`/deduce/schema?kind=${selectedKind}`);
      setSchema(sch.data);
      const { data } = await api.post("/deduce/start", { kind: selectedKind, lang });
      setSessionId(data.session_id);
      setAnswers({});
      setStep(1);
      setResult(null);
      setSaved(false);
      setNarrowing({ candidates: data.candidates, candidates_count: data.candidates.length, total: data.total_items });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("deduce.toastError"));
    } finally { setLoading(false); }
  }, [t, lang]);

  const pickKind = (k) => { setKind(k); start(k); };

  const stepQuestions = useMemo(
    () => (schema?.questions || []).filter((q) => q.step === step),
    [schema, step]
  );

  const allCurrentStepAnswered = stepQuestions.length > 0 && stepQuestions.every((q) => answers[q.id]);
  const eliminated = (narrowing?.total || 0) - (narrowing?.candidates_count || 0);

  const setAns = (qid, val) => {
    setAnswers((a) => {
      const next = { ...a, [qid]: val };
      if (sessionId) {
        api.post("/deduce/answer", { session_id: sessionId, answers: next })
          .then((r) => setNarrowing(r.data))
          .catch(() => {});
      }
      return next;
    });
  };

  const validateStep = async () => {
    if (!sessionId || !allCurrentStepAnswered) return;
    setLoading(true);
    try {
      if (step < 3) {
        setStep(step + 1);
      } else {
        await api.post("/deduce/answer", { session_id: sessionId, answers });
        setFinalizing(true);
        const fin = await api.post("/deduce/finalize", { session_id: sessionId, lang });
        setResult(fin.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("deduce.toastError"));
    } finally { setLoading(false); setFinalizing(false); }
  };

  const backStep = () => { if (step > 1) setStep(step - 1); };

  const reset = (newKind = null) => {
    setSessionId(null); setAnswers({}); setStep(1); setResult(null);
    setNarrowing(null); setSaved(false);
    if (newKind) { setKind(newKind); start(newKind); }
    else { setKind(null); setSchema(null); }
  };

  // ─── Render: kind picker ───────────────────────────────────────────
  if (!kind) {
    const kinds = ["wine", "beer", "spirit"];
    return (
      <div className="max-w-5xl mx-auto px-5 md:px-10 py-10 md:py-14" data-testid="deduce-page">
        <Toaster theme="light" />
        <div className="flex items-center gap-4 mb-8">
          <img src="/brand/audrey-ai.png" alt="geoVINUM AI" className="w-14 h-14 rounded-full object-cover ring-2 ring-[#E5276A]/30" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#E5276A] font-bold">
              {t("deduce.brand")}{!user && <span className="ml-2 text-[#1F3A5F]/80">· {t("deduce.guest")}</span>}
            </div>
            <h1 className="font-display text-3xl md:text-4xl text-[#1F3A5F] leading-tight">{t("deduce.title")}</h1>
            <p className="text-sm text-[#8B95A1]">{user ? t("deduce.subtitleAuthed") : t("deduce.subtitleGuest")}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4" data-testid="deduce-kind-picker">
          {kinds.map((k) => {
            const Ic = KIND_ICONS[k];
            return (
              <button
                key={k}
                onClick={() => pickKind(k)}
                data-testid={`deduce-pick-${k}`}
                className="group text-left app-card p-6 hover:-translate-y-1 transition-all border-2 border-transparent hover:border-[#E5276A]"
              >
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: `${KIND_TINT[k]}14` }}>
                  <Ic className="w-7 h-7" style={{ color: KIND_TINT[k] }} />
                </div>
                <h2 className="font-display text-2xl text-[#1F3A5F] mb-1">{t(`deduce.kind.${k}`)}</h2>
                <p className="text-sm text-[#8B95A1]">{t(`deduce.kindDesc.${k}`)}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-[#E5276A] group-hover:gap-2 transition-all">
                  {t("deduce.startBtn")} <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ─── Render: loading schema ────────────────────────────────────────
  if (!schema) {
    return (
      <div className="max-w-3xl mx-auto px-5 md:px-10 py-16 flex items-center gap-2 text-[#8B95A1]">
        <Loader2 className="w-5 h-5 animate-spin" /> {t("deduce.preparing")}
      </div>
    );
  }

  // ─── Render: questionnaire / verdict ───────────────────────────────
  const kindLabel = t(`deduce.kind.${kind}`);
  return (
    <div className="max-w-7xl mx-auto px-5 md:px-10 py-8 md:py-12" data-testid="deduce-page">
      <Toaster theme="light" />

      <div className="flex items-center gap-4 mb-4">
        <img src="/brand/audrey-ai.png" alt="geoVINUM AI" className="w-14 h-14 rounded-full object-cover ring-2 ring-[#E5276A]/30" />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#E5276A] font-bold">{t("deduce.brand")} · {kindLabel}</div>
          <h1 className="font-display text-3xl md:text-4xl text-[#1F3A5F] leading-tight">
            {t("deduce.titleForKind").replace("{kind}", kindLabel.toLowerCase())}
          </h1>
          <p className="text-sm text-[#8B95A1]">{t("deduce.subtitleQuestions")}</p>
        </div>
        <button onClick={() => reset()} data-testid="deduce-change-kind"
          className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 border-[#1F3A5F]/15 text-xs font-bold text-[#1F3A5F] hover:border-[#E5276A]">
          <RotateCcw className="w-3.5 h-3.5" /> {t("deduce.changeDrink")}
        </button>
      </div>

      {/* Step tabs */}
      <div className="grid grid-cols-3 gap-2 mb-6" data-testid="deduce-stepbar">
        {[1, 2, 3].map((s) => {
          const Sm = stepMeta[s];
          const active = step === s;
          const done = s < step || (s === step && allCurrentStepAnswered && !result);
          return (
            <div key={s} data-testid={`deduce-step-${s}`}
              className={`p-3 rounded-2xl border-2 transition-all ${
                active ? "bg-[#1F3A5F] border-[#1F3A5F] text-white"
                : done ? "bg-white border-[#22C55E]/40 text-[#1F3A5F]"
                : "bg-white border-[#1F3A5F]/10 text-[#8B95A1]"
              }`}>
              <div className="flex items-center gap-2">
                <Sm.icon className={`w-4 h-4 ${active ? "text-white" : done ? "text-[#22C55E]" : "text-[#8B95A1]"}`} />
                <span className="text-[10px] uppercase tracking-wider font-bold">{t("deduce.step")} {s}</span>
                {done && <Check className="w-3 h-3 ml-auto text-[#22C55E]" />}
              </div>
              <div className={`font-display text-lg ${active ? "text-white" : ""}`}>{Sm.label}</div>
              <div className={`text-[11px] ${active ? "text-white/80" : "text-[#8B95A1]"}`}>{Sm.subtitle}</div>
            </div>
          );
        })}
      </div>

      {result && (
        <FinalScreen
          result={result}
          narrowing={narrowing}
          kind={kind}
          kindLabel={kindLabel}
          t={t}
          onReset={() => reset(kind)}
          onChangeKind={() => reset()}
          saving={saving}
          saved={saved}
          onSaveNotes={async () => {
            if (saved) return;
            if (!user) {
              toast.message(t("deduce.toastNudgeGuest"), { duration: 4000 });
              navigate("/register?next=/deduce");
              return;
            }
            setSaving(true);
            try {
              const top = result.top_match;
              const notes = [
                `${t("deduce.deductionSummary")} — ${kindLabel}.`,
                top ? `${t("deduce.likelyProfile")} : ${top.name}${top.region ? `, ${top.region}` : ""}${top.country ? `, ${top.country}` : ""}.` : "",
                top && top.grapes?.length ? `${t("deduce.grapesMatter")} : ${top.grapes.join(", ")}.` : "",
                `${t("deduce.synthesis")} : ${result.verdict}`,
              ].filter(Boolean).join(" ");
              await api.post("/tastings/ai-create", { notes });
              setSaved(true);
              toast.success(t("deduce.toastSaved"));
            } catch (e) {
              toast.error(e?.response?.data?.detail || t("deduce.toastSaveError"));
            } finally { setSaving(false); }
          }}
        />
      )}

      {!result && (
        <div className="grid lg:grid-cols-[1fr,360px] gap-6">
          <div className="space-y-4">
            {stepQuestions.map((q, idx) => (
              <div key={q.id} className="app-card p-5" data-testid={`deduce-q-${q.id}`}>
                <div className="text-[11px] uppercase tracking-wider text-[#E5276A] font-bold mb-1">{t("deduce.question")} {idx + 1}</div>
                <h2 className="font-display text-lg text-[#1F3A5F] mb-3">{q.label}</h2>
                <div className="flex flex-wrap gap-2">
                  {q.options.map((o) => {
                    const sel = answers[q.id] === o.id;
                    return (
                      <button
                        key={o.id}
                        onClick={() => setAns(q.id, o.id)}
                        data-testid={`deduce-opt-${q.id}-${o.id}`}
                        className={`px-3.5 py-2 rounded-full text-sm font-bold transition-all border-2 ${
                          sel
                            ? "bg-[#E5276A] border-[#E5276A] text-white"
                            : "bg-white border-[#1F3A5F]/12 text-[#1F3A5F] hover:border-[#E5276A]"
                        }`}
                      >
                        {sel && <Check className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />}
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="flex items-center gap-2 pt-2">
              {step > 1 && (
                <button onClick={backStep} data-testid="deduce-back"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border-2 border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A]">
                  <ArrowLeft className="w-4 h-4" /> {t("deduce.previousStep")}
                </button>
              )}
              <button
                onClick={validateStep}
                disabled={!allCurrentStepAnswered || loading || finalizing}
                data-testid="deduce-validate"
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#1F3A5F] text-white font-bold hover:bg-[#2A4870] disabled:opacity-50 transition-colors"
              >
                {(loading || finalizing) ? <Loader2 className="w-4 h-4 animate-spin" />
                  : step < 3 ? <>{t("deduce.validateNext").replace("{n}", step + 1)} <ArrowRight className="w-4 h-4" /></>
                  : <><Sparkles className="w-4 h-4" /> {t("deduce.finalVerdict")}</>}
              </button>
            </div>
          </div>

          <aside className="lg:sticky lg:top-4 self-start space-y-4">
            <div className="bg-[#1F3A5F] text-white rounded-2xl p-5" data-testid="deduce-narrowing">
              <div className="text-[10px] uppercase tracking-wider text-[#E5276A] font-bold mb-1">{t("deduce.realtimeResults")}</div>
              <div className="text-xs text-white/70 mb-3">{t("deduce.progress")}</div>
              <div className="flex items-center justify-between mb-5">
                {[1, 2, 3].map((s) => (
                  <div key={s} className="flex flex-col items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      s < step ? "bg-[#22C55E] text-white"
                      : s === step ? "bg-[#E5276A] text-white"
                      : "bg-white/15 text-white/60"
                    }`}>
                      {s < step ? <Check className="w-3.5 h-3.5" /> : s}
                    </div>
                    <span className="text-[10px] mt-1 uppercase tracking-wider">{stepMeta[s].label}</span>
                  </div>
                ))}
              </div>
              <div className="bg-white/8 rounded-xl p-4 text-center">
                <div className="text-[10px] uppercase tracking-wider text-white/70 mb-1">{t("deduce.remaining")}</div>
                <div className="font-display text-4xl text-white">
                  {narrowing?.candidates_count ?? "—"}
                  <span className="text-base text-white/60"> / {narrowing?.total ?? "—"}</span>
                </div>
                <div className="text-xs text-white/70 mt-0.5">{t(`deduce.possibleItems.${kind}`)}</div>
              </div>
              {eliminated > 0 && (
                <div className="mt-3 text-center text-xs text-white/80">
                  <X className="inline w-3.5 h-3.5 text-[#E5276A]" /> {t("deduce.eliminated")} : <b className="text-white">{eliminated}</b>
                </div>
              )}
            </div>

            {narrowing?.candidates?.length > 0 && (
              <div className="bg-white rounded-2xl p-5 border border-[#1F3A5F]/10" data-testid="deduce-candidates">
                <div className="text-[10px] uppercase tracking-wider text-[#E5276A] font-bold mb-2">{t("deduce.possibleProfiles")}</div>
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {narrowing.candidates.slice(0, 8).map((c) => (
                    <div key={c.id} data-testid={`deduce-candidate-${c.id}`} className="flex items-start gap-2 text-sm">
                      <Check className="w-3.5 h-3.5 text-[#22C55E] shrink-0 mt-1" />
                      <div className="min-w-0">
                        <div className="font-bold text-[#1F3A5F] leading-tight">{c.name}</div>
                        <div className="text-[11px] text-[#8B95A1]">
                          {(c.grapes || []).join(", ")}
                          {c.region ? ` · ${c.region}` : ""}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {narrowing.candidates.length > 8 && (
                  <div className="text-[10px] text-[#8B95A1] mt-2">{t("deduce.moreOthers").replace("{n}", narrowing.candidates.length - 8)}</div>
                )}
              </div>
            )}

            <div className="bg-[#FCE4ED]/40 rounded-2xl p-4 text-xs text-[#1F3A5F] flex items-start gap-2">
              <Lightbulb className="w-4 h-4 text-[#E5276A] shrink-0 mt-0.5" />
              <span>{t(`deduce.tip.${kind}`)}</span>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function FinalScreen({ result, kind, kindLabel, t, onReset, onChangeKind, onSaveNotes, saving, saved }) {
  const top = result.top_match;
  const region = top?.region ? encodeURIComponent(top.region) : "";
  return (
    <div className="grid lg:grid-cols-[1fr,360px] gap-6" data-testid="deduce-final">
      <div className="space-y-5">
        <div className="app-card p-6 border-l-4 border-[#E5276A]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-[#FCE4ED] flex items-center justify-center">
              <Trophy className="w-7 h-7 text-[#E5276A]" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#E5276A] font-bold">{t("deduce.verdict")}</div>
              <h2 className="font-display text-2xl text-[#1F3A5F]">
                {top ? top.name : t("deduce.profileIdentified")}
              </h2>
              {top && (
                <div className="text-sm text-[#4A5568]">
                  {(top.grapes || []).join(", ")}{top.region ? ` · ${top.region}` : ""}{top.country ? `, ${top.country}` : ""}
                </div>
              )}
            </div>
          </div>
          <div className="whitespace-pre-line text-[#1F3A5F] leading-relaxed text-sm" data-testid="deduce-verdict">
            {result.verdict}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={onSaveNotes} disabled={saving || saved} data-testid="deduce-save-notes"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#22C55E] text-white text-sm font-bold hover:bg-[#16A34A] disabled:opacity-60">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("deduce.saving")}</>
              : saved ? <><Check className="w-4 h-4" /> {t("deduce.saved")}</>
              : <><BookmarkPlus className="w-4 h-4" /> {t("deduce.saveToNotes")}</>}
          </button>
          {top?.region && kind === "wine" && (
            <a href={`/shop?search=${region}`} data-testid="deduce-buy-map"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#1F3A5F] text-white text-sm font-bold hover:bg-[#2A4870]">
              <Map className="w-4 h-4" /> {t("deduce.buyMap")}
            </a>
          )}
          <button onClick={onReset} data-testid="deduce-restart"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661]">
            <RotateCcw className="w-4 h-4" /> {t("deduce.newDeduction")}
          </button>
          <button onClick={onChangeKind} data-testid="deduce-change-kind-bottom"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border-2 border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A]">
            <Wine className="w-4 h-4" /> {t("deduce.changeDrink")}
          </button>
          <a href="/play"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border-2 border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A]">
            {t("deduce.otherGames")}
          </a>
        </div>
      </div>

      <aside className="space-y-4">
        <div className="bg-white rounded-2xl p-5 border border-[#1F3A5F]/10">
          <div className="text-[10px] uppercase tracking-wider text-[#E5276A] font-bold mb-2">{t("deduce.compatibleProfiles")}</div>
          <div className="space-y-2">
            {(result.candidates || []).map((c) => (
              <div key={c.id} className="flex items-start gap-2 text-sm">
                <Check className="w-3.5 h-3.5 text-[#22C55E] shrink-0 mt-1" />
                <div>
                  <div className="font-bold text-[#1F3A5F] leading-tight">{c.name}</div>
                  <div className="text-[11px] text-[#8B95A1]">{(c.grapes || []).join(", ")}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
}
